import {Letter, TileCoordinates, TileCoordinateType, TileData} from "./Tile";
import {useEffect, useRef, useState} from "react";
import {Grid} from "./Grid";
import {addNTimes, DICTIONARY_SET, generateLetterList, GRID_LENGTH, TRAY_LENGTH} from "./constants";
import {coalesce, randomizeArray, scoreGrid, WordScore} from "./utils";
import {Tray} from "./Tray";
import {AI, performMove} from "./ai";
import {Modal} from "./Modal";

const EMPTY_GRID_TILEDATA: Array<TileData> = [];
const EMPTY_TRAY_TILEDATA: Array<TileData> = [];

{
    for (let i = 0; i < GRID_LENGTH * GRID_LENGTH; i++) {
        EMPTY_GRID_TILEDATA.push(null);
    }

    for (let i = 0; i < GRID_LENGTH; i++) {
        EMPTY_TRAY_TILEDATA.push(null);
    }
}

const enum Player {
    Player = 'Player',
    AI = 'AI',
}

export function Game(props: {
    proportionDictionary: number,
    aiRandomness: number,
    endGame: () => void,

}) {
    const [gridTileData, setGridTileData] = useState(() => EMPTY_GRID_TILEDATA.slice());
    const [playerTrayTileData, setPlayerTrayTileData] = useState(() => EMPTY_TRAY_TILEDATA.slice());
    const [aiTrayTileData, setAITrayTileData] = useState(() => EMPTY_TRAY_TILEDATA.slice());
    const [remainingLetters, setRemainingLetters] = useState(() => {
        const letter_order = generateLetterList();
        randomizeArray(letter_order);
        return letter_order;
    });

    let [turnNumber, setTurnNumber] = useState(1);
    let [playerScore, setPlayerScore] = useState(0);
    let [aiScore, setAIScore] = useState(0);
    const [logInfo, setLogInfo] = useState([<h4 key={0}>Turn 1</h4>]);
    const [addToDictionarySuggestion, setAddToDictionarySuggestion] = useState<string>(null);
    const [timesPlayerPassed, setTimesPlayerPassed] = useState<number>(0);
    const [timesAIPassed, setTimesAIPassed] = useState<number>(0);

    const [AIObject, setAIObject] = useState(() => {
        return new AI(props.proportionDictionary, props.aiRandomness);
    });
    const [isTileExchangeOpen, setIsTileExchangeOpen] = useState<boolean>(false);
    const [tilesToExchange, setTilesToExchange] = useState<Array<boolean>>(() => {
        const array: Array<boolean> = [];
        addNTimes(array, false, TRAY_LENGTH);
        return array;
    });

    const isGameOverRef = useRef<boolean>(false);

    const [errorText, setErrorText] = useState<string>(null);

    const [playerTurn, setPlayerTurn] = useState<Player>(null);

    const logDivRef = useRef(null);

    let countAITiles = 0;
    let countPlayerTiles = 0;
    for(let i=0; i<TRAY_LENGTH; i++){
        if(aiTrayTileData[i] != null){
            countAITiles++;
        }
        if(playerTrayTileData[i] != null){
            countPlayerTiles++;
        }
    }

    function fillInTray(coordinate: TileCoordinateType) {
        let tray: Array<TileData>, updateTray: Function;
        switch (coordinate) {
            case TileCoordinateType.GRID:
                console.error("GRID should never have been an argument to fill in tray");
                [tray, updateTray] = [gridTileData, setGridTileData];
                break;
            case TileCoordinateType.TRAY_PLAYER:
                [tray, updateTray] = [playerTrayTileData, setPlayerTrayTileData];
                break;
            case TileCoordinateType.TRAY_AI:
                [tray, updateTray] = [aiTrayTileData, setAITrayTileData];
                break;
        }

        const startingLetterLength = remainingLetters.length;

        for (let i = 0; i < TRAY_LENGTH; i++) {
            const currentTileData = tray[i];
            if (currentTileData == null && remainingLetters.length > 0) {
                tray[i] = {
                    isEphemeral: false,
                    coordinates: {
                        location: coordinate,
                        index: i
                    },
                    letter: remainingLetters.pop(),
                    recentlyPlayed: false,
                };
            }
        }

        if (remainingLetters.length != startingLetterLength) {
            updateTray(tray.slice());
            setRemainingLetters(remainingLetters.slice());

        }
    }

    function modifyTileData(movedToCoordinates: TileCoordinates, movedTileData: TileData) {
        const oldCoordinates = movedTileData.coordinates;

        let newCoordinatesTileData: Array<TileData> = null;
        let setNewCoordinatesTileData: (x: Array<TileData>) => void = null;
        let displacedTileData: TileData;
        if (movedToCoordinates.location == TileCoordinateType.GRID) {
            movedTileData.isEphemeral = true;
            newCoordinatesTileData = gridTileData;
            setNewCoordinatesTileData = setGridTileData;
        } else if (movedToCoordinates.location == TileCoordinateType.TRAY_PLAYER) {
            movedTileData.isEphemeral = false;
            newCoordinatesTileData = playerTrayTileData;
            setNewCoordinatesTileData = setPlayerTrayTileData;
        } else if (movedToCoordinates.location == TileCoordinateType.TRAY_AI) {
            movedTileData.isEphemeral = false;
            newCoordinatesTileData = aiTrayTileData;
            setNewCoordinatesTileData = setAITrayTileData;
        }
        displacedTileData = newCoordinatesTileData[movedToCoordinates.index];
        if (displacedTileData != null) {
            displacedTileData.coordinates = oldCoordinates;

            if(oldCoordinates.location == TileCoordinateType.GRID){
                displacedTileData.isEphemeral = true;
            } else {
                displacedTileData.isEphemeral = false;
            }

        }

        // First delete the old tile
        if (oldCoordinates.location == TileCoordinateType.GRID) {
            gridTileData[oldCoordinates.index] = displacedTileData;
            setGridTileData(gridTileData.slice());
        } else if (oldCoordinates.location == TileCoordinateType.TRAY_PLAYER) {
            playerTrayTileData[oldCoordinates.index] = displacedTileData;
            setPlayerTrayTileData(playerTrayTileData.slice());
        } else if (oldCoordinates.location == TileCoordinateType.TRAY_AI) {
            aiTrayTileData[oldCoordinates.index] = displacedTileData;
            setAITrayTileData(aiTrayTileData.slice());
        }

        // Next set the new tile
        movedTileData.coordinates = movedToCoordinates;
        newCoordinatesTileData[movedToCoordinates.index] = movedTileData;
        setNewCoordinatesTileData(newCoordinatesTileData.slice());

    }

    function grabLettersToTray(player: Player){
        let tray;
        let updateTrayFunction;
        let trayDestination;
        if (player == Player.Player) {
            tray = playerTrayTileData;
            updateTrayFunction = setPlayerTrayTileData;
            trayDestination = TileCoordinateType.TRAY_PLAYER;
        } else {
            tray = aiTrayTileData;
            updateTrayFunction = setAITrayTileData;
            trayDestination = TileCoordinateType.TRAY_AI
        }

        let lettersToTray = false;
        for (let i = 0; i < TRAY_LENGTH; i++) {
            const trayTile = tray[i];
            if (trayTile == null && remainingLetters.length > 0) {
                lettersToTray = true;
                const newLetter = remainingLetters.pop();
                tray[i] = {
                    isEphemeral: false,
                    coordinates: {
                        location: trayDestination,
                        index: i,
                    },
                    letter: newLetter,
                };
            }
        }

        if (lettersToTray) {
            updateTrayFunction(tray.slice());
            setRemainingLetters(remainingLetters.slice());
        }
    }

    function endTurn(player: Player):boolean {
        try {
            const scores: Array<WordScore> = scoreGrid(gridTileData);
            let totalTurnScore: number = 0;
            for (let i = 0; i < scores.length; i++) {
                const score = scores[i];
                logInfo.push(<div key={logInfo.length}>{player} received {score.score} points for playing '{score.word}.'</div>);
                totalTurnScore += score.score;
            }
            logInfo.push(<div key={logInfo.length}>{player} received a total of <strong>{totalTurnScore} points</strong> for their
                turn.</div>);

            if (player == Player.Player) {
                setPlayerScore(playerScore + totalTurnScore);
            } else {
                setAIScore(aiScore + totalTurnScore);
            }

            for (let i = 0; i < GRID_LENGTH * GRID_LENGTH; i++) {
                const tileData = gridTileData[i];

                if (tileData != null) {
                    tileData.isEphemeral = false;
                }
            }
            setGridTileData(gridTileData.slice())

            grabLettersToTray(player);


            if(player == Player.Player){
                setTimesPlayerPassed(0);
            } else if(player == Player.AI){
                setTimesAIPassed(0);
            } else {
                console.error({
                    message: "Unknown player",
                    player: player,
                });
            }

            performEndTurnFunctions();
            return true;

        } catch (e) {
            // Exception should only be thrown from scoreGrid
            // e should be of type Error from utils.ts
            console.error({e});
            if(e.type == 'InvalidWord'){
                setAddToDictionarySuggestion(e.word);
            } else {
                //window.alert(e.message);
                setErrorText(e.message);
            }

            return false;
        }


    }

    function performEndTurnFunctions(){
        logInfo.push(<h4 key={logInfo.length}>Turn {turnNumber + 1}</h4>);
        setLogInfo(logInfo.slice());

        // Finally, iterate what turn it is
        // note - we iterate it locally because the AI will immediately get run next and so turnNumber won't have had a chance yet to
        // be updated through setTurnNumber
        turnNumber = turnNumber + 1;
        setTurnNumber(turnNumber);
    }

    function passTurn(player: Player, autoPass: boolean = false){
        if(player == Player.AI){
            setTimesAIPassed(timesAIPassed + 1);
            setPlayerTurn(Player.Player);
        } else {
            setTimesPlayerPassed(timesPlayerPassed + 1);
            setPlayerTurn(Player.AI);
        }
        if(autoPass){
            logInfo.push(<div key={logInfo.length}>{player} passed because they have no more tiles</div>);
        } else {
            logInfo.push(<div key={logInfo.length}>{player} passed</div>);
        }

        performEndTurnFunctions();

    }


    function aiMove(){
        if(!isGameOverRef.current) {
            const bestMove = AIObject.findBestMove(gridTileData, aiTrayTileData);
            setAIObject(AIObject);

            // remove the last turn's colorings
            for (let i = 0; i < GRID_LENGTH * GRID_LENGTH; i++) {
                const tileData = gridTileData[i];
                if (tileData != null) {
                    tileData.recentlyPlayed = false;
                }
            }
            setGridTileData(gridTileData.slice());

            if (bestMove != null) {
                // move is available, perform it and move on
                performMove(gridTileData, aiTrayTileData, bestMove);
                endTurn(Player.AI);

            } else {
                // no valid move; plan will be to exchange letters if any remain. Otherwise pass.
                if (remainingLetters.length > 0) {
                    exchangeTiles(Player.AI, aiTrayTileData);

                } else {
                    passTurn(Player.AI);
                }
            }
        }
    }

    function returnTilesToTray(){
        for(let i=0; i<GRID_LENGTH; i++){
            for(let j=0; j<GRID_LENGTH; j++){
                const key = i*GRID_LENGTH + j;
                const tile: TileData = gridTileData[key];

                if(tile != null){
                    if(tile.isEphemeral){
                        // find first free spot in player tray

                        let foundFreeSpot = false;
                        for(let k=0; k<TRAY_LENGTH; k++){
                            const trayTile: TileData = playerTrayTileData[k];
                            if(trayTile == null){
                                modifyTileData({
                                    index: k,
                                    location: TileCoordinateType.TRAY_PLAYER,
                                }, tile);
                                foundFreeSpot = true;
                                break;
                            }
                        }

                        if(!foundFreeSpot){
                            console.error({
                                message: "Unable to find a free spot in player tray to return tile to.",
                                tile: tile,
                            });
                        }
                    }

                }


            }
        }
    }

    function exchangeTiles(player: Player, tiles: Array<TileData>){
        let lettersExchanged = 0;
        for(let i=0; i<tiles.length; i++){
            const tile = tiles[i];
            if(tile == null){
                // nulls are allowed in the tray array so we need to handle that
                continue;
            }

            lettersExchanged++;

            const letter = tile.letter;
            const coordinates = tile.coordinates;
            if(letter.is_blank){
                letter.str = '';
            }
            remainingLetters.push(letter);

            // Delete the old tile
            if (coordinates.location == TileCoordinateType.GRID) {
                gridTileData[coordinates.index] = null;
                setGridTileData(gridTileData.slice());
            } else if (coordinates.location == TileCoordinateType.TRAY_PLAYER) {
                playerTrayTileData[coordinates.index] = null;
                setPlayerTrayTileData(playerTrayTileData.slice());
            } else if (coordinates.location == TileCoordinateType.TRAY_AI) {
                aiTrayTileData[coordinates.index] = null;
                setAITrayTileData(aiTrayTileData.slice());
            }
        }

        randomizeArray(remainingLetters);
        grabLettersToTray(player); // note that this also resets remainingLetters

        logInfo.push(<div key={logInfo.length}>{player} exchanged {lettersExchanged} tiles for their turn.</div>);
        performEndTurnFunctions();

        if(player == Player.Player){
            setTimesPlayerPassed(0);
        } else {
            setTimesAIPassed(0);
        }

    }

    // On startup only
    useEffect(() => {
        fillInTray(TileCoordinateType.TRAY_PLAYER);
        fillInTray(TileCoordinateType.TRAY_AI);

        if(Math.random() < 0.5){
            // AI starts first
            logInfo.push(<div key={1}>AI goes first</div>);
            setPlayerTurn(Player.AI);
            //aiMove();
        } else {
            logInfo.push(<div key={1}>Player goes first</div>);
            setPlayerTurn(Player.Player);
        }

        setLogInfo(logInfo.slice());

    }, [])

    useEffect(() => {
        // Effect is to keep the log window scrolled down
        if (logDivRef.current != null) {
            logDivRef.current.scrollTo(0, logDivRef.current.scrollHeight); // scroll down
        }
    }, [logInfo])

    // Effect is to detect when game is over and bring up window
    useEffect(() => {
        if(!isGameOverRef.current && (timesPlayerPassed == 2 || timesAIPassed == 2)){
            logInfo.push(<h4 key={logInfo.length}>Scoring</h4>);
            let [playerPenalty, aiPenalty] = [0, 0];
            let playerLetters: Letter[] = [];
            let aiLetters: Letter[] = [];

            for(let i=0; i<TRAY_LENGTH; i++){
                let playerTile = playerTrayTileData[i];
                if(playerTile != null){
                    playerPenalty += playerTile.letter.points;
                    playerLetters.push(playerTile.letter);
                    playerTrayTileData[i] = null;
                }
                let aiTile = aiTrayTileData[i];
                if(aiTile != null){
                    aiPenalty += aiTile.letter.points;
                    aiLetters.push(aiTile.letter);
                    aiTrayTileData[i] = null;
                }
            }

            setPlayerTrayTileData(playerTrayTileData.slice());
            setAITrayTileData(aiTrayTileData.slice());

            playerScore = playerScore - playerPenalty;
            setPlayerScore(playerScore);

            aiScore = aiScore - aiPenalty;
            setAIScore(aiScore);

            const parseLetters = function(list: Letter[]): string{
                let text = '';
                for(let i=0; i<list.length; i++){
                    const letter = list[i];
                    const letterText = letter.is_blank ? "a blank" : letter.str;

                    if(i == 0){
                        text = letterText;
                    } else if(i == list.length - 1 && i > 0){
                        if(list.length == 2){
                            text = `${text} and ${letterText}`;
                        } else {
                            // Oxford comma
                            text = `${text}, and ${letterText}`;
                        }

                    } else{
                        text = `${text}, ${letterText}`;
                    }
                }

                return text;
            }

            if(playerLetters.length > 0){
                logInfo.push(<div key={logInfo.length}>Player penalized {playerPenalty} points for not using {parseLetters(playerLetters)}.</div>);
            } else {
                logInfo.push(<div key={logInfo.length}>Player finished with no remaining tiles.</div>);
            }

            if(aiLetters.length > 0){
                logInfo.push(<div key={logInfo.length}>AI penalized {aiPenalty} points for not using {parseLetters(aiLetters)}.</div>);
            } else {
                logInfo.push(<div key={logInfo.length}>AI finished with no remaining tiles.</div>);
            }

            const winner = playerScore > aiScore ? 'Player' : 'AI';
            let message = `Game over - ${winner} won!`;
            if(playerScore == aiScore){
                message = "Game over - it's a tie!"
            }

            logInfo.push(<h4 key={logInfo.length}>{message}</h4>);
            setLogInfo(logInfo.slice());
            isGameOverRef.current = true;

            //window.alert(message);
        }

    }, [timesPlayerPassed, timesAIPassed, aiTrayTileData, playerTrayTileData])


    // Play AI on its turn, or auto-pass the player if there are no more tiles
    useEffect(() => {

        if(playerTurn == Player.AI){
            aiMove();
            setPlayerTurn(Player.Player);
        } else {
            // Player turn just started; let's count how many tiles the player has
            if(countPlayerTiles == 0 && remainingLetters.length == 0 && !isGameOverRef.current){
                // there are no tiles for the player, so we'll just treat it as a skip
                passTurn(Player.Player, true);
            }
        }

    }, [playerTurn])


    const isDebug: boolean = coalesce(window['debug'], false);

    let tilesExchangedSelected = 0;
    for(let i=0; i<TRAY_LENGTH; i++){
        if(tilesToExchange[i]){
            tilesExchangedSelected++;
        }
    }

    function closeTileExchangeModal(){
        setIsTileExchangeOpen(false);
        const array = [];
        addNTimes(array, false, TRAY_LENGTH);
        setTilesToExchange(array);
    }


    return (
        <>
            {/* Dictionary add modal */}
            <Modal
                isOpen={addToDictionarySuggestion != null}
                setClosed={() => setAddToDictionarySuggestion(null)}
            >
                <div className="add-to-dictionary-dialog">
                    <div><b>{addToDictionarySuggestion}</b> isn't in the dictionary. Would you like to add it for this game?</div>
                    <div className="buttons">
                        <button onClick={(e) =>{
                            DICTIONARY_SET.add(addToDictionarySuggestion);
                            setAddToDictionarySuggestion(null);
                        }}>
                            Yes
                        </button>
                        <button onClick={(e) => {setAddToDictionarySuggestion(null);}}>
                            No
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Tile exchange modal */}
            <Modal
                isOpen={isTileExchangeOpen}
                setClosed={() => {closeTileExchangeModal();}}
            >
                <div className="tile-exchange-dialog">
                    <div className="instructions">
                        Click on each tile you'd like to exchange. You currently have {tilesExchangedSelected} tiles selected.
                    </div>
                    <div className="selection-buttons">
                        <button onClick={(e) => {
                            const array = [];
                            addNTimes(array, true, TRAY_LENGTH);
                            setTilesToExchange(array);
                        }}>Select All</button>
                        <button onClick={(e) => {
                            const array = [];
                            addNTimes(array, false, TRAY_LENGTH);
                            setTilesToExchange(array);
                        }}>Select None</button>
                    </div>
                    <TilesExchangedTray tray={playerTrayTileData} selectedArray={tilesToExchange} setSelectedArray={setTilesToExchange} />
                    <div className="finish-buttons">
                        <button onClick={() => {closeTileExchangeModal();}}>Cancel</button>
                        <button disabled = {tilesExchangedSelected == 0} onClick={(e) => {
                            const array: Array<TileData> = [];

                            for(let i=0; i<TRAY_LENGTH; i++){
                                if(tilesToExchange[i]){
                                    array.push(playerTrayTileData[i]);
                                }
                            }

                            exchangeTiles(Player.Player, array);
                            closeTileExchangeModal();
                            setPlayerTurn(Player.AI);
                            //aiMove();

                        }}>Exchange</button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={errorText != null}
                setClosed={() => setErrorText(null)}
                >
                <div className="error-dialog">
                    <h3>Error</h3>
                    <div className="error-text">
                        {errorText}
                    </div>
                    <div className="buttons">
                        <button onClick={(e) => setErrorText(null)}>OK</button>
                    </div>
                </div>

            </Modal>
            <div className="game">
                <div className="board-tray">
                    <Grid grid_tiles={gridTileData} modifyTileStore={modifyTileData}/>
                    <div className="player-tray-row">
                        <div>
                            <div>{remainingLetters.length} letters remaining</div>
                            {
                                isDebug ?
                                <button onClick={(e) => {
                                    setRemainingLetters([]);
                                }}>Delete letters</button> : null
                            }
                            <div>AI has {countAITiles} tiles</div>
                            <button onClick={(e) => {
                                returnTilesToTray();
                                setIsTileExchangeOpen(true);
                            }}
                            disabled={remainingLetters.length == 0 || isGameOverRef.current}
                            >Open Tile Exchange</button>
                        </div>

                        <Tray tiles={playerTrayTileData} modifyTileStore={modifyTileData}
                              trayCoordinateType={TileCoordinateType.TRAY_PLAYER}/>
                        <div className="player-controls">
                            <button
                                onClick={(e) => {
                                    if(endTurn(Player.Player)){
                                        setPlayerTurn(Player.AI);
                                        //aiMove();
                                    }
                                }}
                                disabled={isGameOverRef.current}
                                className="end-turn"
                            >End Turn</button>
                            <button
                                onClick={(e) => {returnTilesToTray();}}
                                disabled={isGameOverRef.current}
                                className="return"
                            >Return Tiles</button>
                            <button
                                onClick={(e) => {
                                    if(window.confirm("Are you sure you want to pass?")){
                                        returnTilesToTray();
                                        passTurn(Player.Player);
                                        //aiMove();
                                    }
                                }}
                                disabled={isGameOverRef.current}
                                className="pass"
                            >Pass</button>

                        </div>

                    </div>
                </div>
                <div className="controls">
                    <div className="buttons">
                        <button onClick={(e) => {
                            props.endGame()
                        }}>End Game
                        </button>
                    </div>
                    <div className="scoring">
                        <div>
                            <h1>Player</h1>
                            <span>{playerScore}</span>
                        </div>
                        <div>
                            <h1>AI</h1>
                            <span>{aiScore}</span>
                        </div>
                    </div>
                    <div className="log" ref={logDivRef}>
                        {logInfo}
                    </div>
                    { // AI options; only relevant if debugging
                        isDebug ?
                            <div className="ai-tray">
                                <h4>AI Tray</h4>
                                <Tray tiles={aiTrayTileData} modifyTileStore={modifyTileData}
                                      trayCoordinateType={TileCoordinateType.TRAY_AI}/>
                                <button onClick={(e) => {
                                    endTurn(Player.AI);
                                }}>End AI Turn</button>
                                <button onClick={() => {aiMove()}}>Run AI</button>
                            </div>
                            : null
                    }
                </div>

            </div>
        </>
    );


}

function DummyExchangeTile(props: {
    letter: Letter,
    isSelected: boolean,
    onClick: () => void,
}){

    // Need to handle blanks
    let letterClass = "text";
    if(props.letter.is_blank){
        letterClass = "blank-input";
    }

    let tileClass = "tile-with-letter";
    if(props.isSelected){
        tileClass = tileClass + ' selected-tile';
    }

    return(
        <div
            className={tileClass}
            key={2}
            onClick={props.onClick}
        >
            <div className={letterClass}>{props.letter.str}</div>
            <div className="value">{props.letter.points}</div>
        </div>
    );

}

function TilesExchangedTray(props: {
    tray: Array<TileData>,
    selectedArray: Array<boolean>,
    setSelectedArray: (x: Array<boolean>) => void,
}){

    const divContent = [];
    for(let i = 0; i<TRAY_LENGTH; i++){
        const tileData = props.tray[i];

        const toggleFunction = () => {
            props.selectedArray[i] = !props.selectedArray[i];
            props.setSelectedArray(props.selectedArray.slice());
        }

        if(tileData != null){
            divContent.push(
                <DummyExchangeTile key={i} letter={tileData.letter} isSelected={props.selectedArray[i]} onClick={toggleFunction}/>
            );
        } else{
            divContent.push(<span key={i} />);
        }
    }

    return (
        <div className="tray-container">
            <div className="tray">
                {divContent}
            </div>
        </div>

    );

}

